import { DownOutlined } from '@ant-design/icons';
import { Button, Popover, Tag } from 'antd';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  OpenClass,
  OpenClassCertificateType,
} from '../../openClasses/types/OpenClass';
import { certificateTypeLabel } from '../../openClasses/utils/certificateTypeLabel';
import {
  isOpenClassAssignable,
  openClassOptionLabel,
  openClassesById,
} from '../../openClasses/utils/openClass';
import {
  ParticipantDraft,
  RegistrationDraft,
  draftCertificateTypes,
} from '../utils/openClassParticipantDraft';

// Keep the existing certificate visible even when its open class is unavailable.
const getAvailableRegistrationTemplates = (
  registration: Pick<
    RegistrationDraft,
    'certificateTemplateId' | 'certificateType'
  >,
  openClass?: Pick<OpenClass, 'certificateTemplates'>,
) =>
  openClass?.certificateTemplates ?? [
    {
      id: registration.certificateTemplateId,
      type: registration.certificateType,
    },
  ];

type OpenClassParticipantCertificatePickerProps = {
  canUpdate: boolean;
  draft: ParticipantDraft;
  openClasses: OpenClass[];
  onChange: (
    registrationKey: string,
    certificateTemplateId: number,
    certificateType: OpenClassCertificateType,
  ) => void;
};

const OpenClassParticipantCertificatePicker: React.FC<
  OpenClassParticipantCertificatePickerProps
> = ({ canUpdate, draft, openClasses, onChange }) => {
  const { t } = useTranslation();
  const byId = useMemo(() => openClassesById(openClasses), [openClasses]);

  if (draft.registrations.length === 0) return <>–</>;

  const certificateTypes = draftCertificateTypes(draft);
  return (
    <Popover
      content={
        <div className="flex min-w-56 flex-col gap-3">
          {draft.registrations.map((registration) => {
            const openClass = byId.get(registration.openClassId);
            const editable =
              canUpdate && !!openClass && isOpenClassAssignable(openClass);
            return (
              <div key={registration.key}>
                <div className="mb-1 text-xs font-medium text-blueGrayDark">
                  {openClass ? openClassOptionLabel(openClass) : '–'}
                </div>
                <div className="flex flex-wrap gap-1">
                  {getAvailableRegistrationTemplates(
                    registration,
                    openClass,
                  ).map((template) => (
                    <Button
                      key={template.id}
                      className="rounded-full"
                      disabled={!editable}
                      size="small"
                      type={
                        registration.certificateTemplateId === template.id
                          ? 'primary'
                          : 'default'
                      }
                      onClick={() =>
                        registration.certificateTemplateId !== template.id &&
                        onChange(registration.key, template.id, template.type)
                      }>
                      {certificateTypeLabel(template.type)}
                    </Button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      }
      title={t('views.OpenClasses.certificateType')}
      trigger="click">
      <Button className="flex h-auto items-center gap-1 px-1" type="text">
        {certificateTypes.map((certificateType) => (
          <Tag
            key={certificateType}
            className="m-0 border-0 bg-blueGrayLight text-safeLifeMedium">
            {certificateTypeLabel(certificateType)}
          </Tag>
        ))}
        <DownOutlined className="text-xs text-grayMedium" />
      </Button>
    </Popover>
  );
};

export default OpenClassParticipantCertificatePicker;
